import { retrieveToken } from '../storage';
import {
  wrapFetchWithDetailedErrors,
  ApiError,
  apiFetch,
  OPERAZIONE_NON_CONSENTITA,
} from './api';

const BASE_URL = process.env.REACT_APP_BACKEND_ENDPOINT;
const PATH = '/api/iot/devices';

// Il registro MVP1 del legame sonda-attrezzatura (`iot_device.equipment_id`), che
// non e' l'Assegnazione: quello vive in `iot_assegnazioni_api.js`, e il commento di
// quel modulo dice che copre le rotte dell'Assegnazione e nient'altro.
//
// Sta qui perche' `POST /api/iot/assegnazioni` non registra la sonda: la risolve a
// partire dall'attrezzatura e rifiuta se non la trova
// (`IotAssegnazioneCrudService.sondaRegistrata`). Associare dal pannello vuol dire
// quindi due scritture, nell'ordine — la seconda pretende che la prima sia avvenuta
// — che e' la stessa sequenza di `IotService.assignProbe` in bs-safe-app.

// I due conflitti che sciogliere un legame risolve. Sono due situazioni diverse e
// portano a due conferme diverse: nel primo caso e' la sonda a essere altrove, nel
// secondo e' l'attrezzatura ad avere gia' un'altra sonda.
export const CODICE_SONDA_GIA_LEGATA = 'ERR-003';
export const CODICE_ATTREZZATURA_GIA_LEGATA = 'ERR-004';

const CODICI_CONFLITTO = [
  CODICE_SONDA_GIA_LEGATA,
  CODICE_ATTREZZATURA_GIA_LEGATA,
];

/**
 * Il codice del conflitto del registro, o `null` per ogni altro rifiuto.
 *
 * Letto da `details` e non dal messaggio: `message` e' il secchio generico del
 * gestore delle eccezioni — «Record gia' presente» per entrambi i codici — quindi
 * non distingue i due casi, ed e' proprio la distinzione che serve per sapere quale
 * conferma mostrare.
 */
export const codiceConflittoRegistro = (error) =>
  CODICI_CONFLITTO.find((codice) => error?.codici?.includes(codice)) ?? null;

class ErroreRegistroSonde extends ApiError {
  constructor(status, message, codici) {
    super(status, message);
    this.codici = codici ?? [];
  }
}

// Not `apiError`: the registry routes raise `ErroreRegistroSonde`, which
// carries the `ERR-00x` codes from `details` alongside the message, and the
// callers read those codes. One factory for the whole module.
const errorFunction = (status, detailedMessage, details) => {
  if (detailedMessage) {
    return new ErroreRegistroSonde(status, detailedMessage, details);
  }
  if (status === 403) {
    return new ErroreRegistroSonde(status, OPERAZIONE_NON_CONSENTITA, details);
  }
  return new ErroreRegistroSonde(status, null, details);
};

const request = (url, method, body) =>
  wrapFetchWithDetailedErrors(
    apiFetch(url, {
      method,
      headers: {
        Authorization: `Bearer ${retrieveToken()}`,
        ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      },
      withCredentials: true,
      credentials: 'include',
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    }),
    errorFunction,
  );

/**
 * Lega la sonda all'attrezzatura nel registro. `PUT` e non `POST`: la rotta e' un
 * upsert idempotente — con lo stesso legame risponde la riga che c'e' gia'.
 *
 * `forceDetach` non e' un default: scioglie un legame di registro precedente, quindi
 * lo accende soltanto una conferma esplicita del tecnico, dopo che il conflitto e'
 * tornato dal server.
 *
 * `idAttrezzatura` viaggia come stringa perche' `RegisterDeviceIotVM` la dichiara
 * `@NotBlank` su `String`, e il MAC come e' scritto sulla sonda: la
 * canonicalizzazione e' del server (ADR-0013).
 */
export const registraSondaSuAttrezzatura = ({
  macAddress,
  attrezzaturaId,
  forceDetach = false,
}) =>
  request(BASE_URL + PATH, 'PUT', {
    idDevice: macAddress,
    idAttrezzatura: String(attrezzaturaId),
    forceDetach,
  });

/**
 * La riga di registro di una sonda, con l'attrezzatura a cui e' legata. Serve alla
 * conferma di `ERR-003`, che deve dire *quale* legame si sta sciogliendo e non un
 * generico «forzare?».
 */
export const leggiSondaDelRegistro = (macAddress) =>
  request(`${BASE_URL}${PATH}/${encodeURIComponent(macAddress)}`, 'GET');

/**
 * Deletes the registry row of a sonda, so the Sonda-Attrezzatura link ends with the
 * disassociation the glossary names after it.
 *
 * Only after the Assegnazione is gone: `eliminaRegistro` refuses with 400 while a
 * live one stands on that sonda (BSDEVPLAT-124), and `IotAssegnazioneCrudService`
 * is the only writer that emits the `RIMOSSA` trigger of ADR-0012 - a removal that
 * started from the registry would leave iot-compliance alerting on a sonda nobody
 * assigned.
 *
 * The MAC travels as the panel holds it: canonicalization is the server's
 * (ADR-0013), and this route resolves the row by canonical form.
 */
export const eliminaSondaDalRegistro = (macAddress) =>
  request(`${BASE_URL}${PATH}/${encodeURIComponent(macAddress)}`, 'DELETE');
